import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from "../config/api";
import { ItemOrderControls, ItemOrderMessage, useItemOrdering } from '../components/ItemOrderControls';
import {
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  CardTitle,
  Row,
  Col,
  Table,
  Form,
  FormGroup,
  Input,
  Button,
} from 'reactstrap';

const moveArrayItem = (items, index, direction) => {
  const targetIndex = index + direction;
  if (index < 0 || targetIndex < 0 || index >= items.length || targetIndex >= items.length) {
    return items;
  }
  const nextItems = [...items];
  [nextItems[index], nextItems[targetIndex]] = [nextItems[targetIndex], nextItems[index]];
  return nextItems;
};

const GalleryMediaOrderEditor = ({ title, items = [], onMove, onRemove, renderPreview }) => {
  if (!items.length) return null;

  return (
    <div style={{ marginTop: '12px', marginBottom: '16px' }}>
      <strong>{title}</strong>
      {items.map((item, index) => {
        const file = item instanceof File ? item : item.file;
        const existingPath = typeof item === 'string' ? item : item.value;
        return (
          <div
            key={item.key || existingPath || `${file?.name}-${index}`}
            style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px', marginTop: '8px', border: '1px solid #ddd', borderRadius: '6px', background: '#fff' }}
          >
            <strong style={{ minWidth: '28px' }}>{index + 1}</strong>
            <div style={{ flex: 1 }}>
              {existingPath ? renderPreview(existingPath, `gallery-order-${index}`, { width: '90px', height: '70px', objectFit: 'cover' }) : <span>{file?.name}</span>}
            </div>
            <div style={{ whiteSpace: 'nowrap' }}>
              <Button type="button" size="sm" color="secondary" disabled={index === 0} onClick={() => onMove(index, -1)}>↑</Button>{' '}
              <Button type="button" size="sm" color="secondary" disabled={index === items.length - 1} onClick={() => onMove(index, 1)}>↓</Button>{' '}
              {onRemove && <Button type="button" size="sm" color="danger" onClick={() => onRemove(item)}>Remove</Button>}
            </div>
          </div>
        );
      })}
    </div>
  );
};

function Gallery() {
  const [images, setImages] = useState([]);
  const [newMainImage, setNewMainImage] = useState(null);
  const [additionalImages, setAdditionalImages] = useState([]);
  const [newTitle, setNewTitle] = useState('');
  const [newTitleAr, setNewTitleAr] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newDescriptionAr, setNewDescriptionAr] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [galleryCount, setgalleryCount] = useState(0);
  const [showUpdateForm, setShowUpdateForm] = useState(false);
  const [updateGallery, setUpdateGallery] = useState({
    id: null,
    title: '',
    title_ar: '',
    description: '',
    description_ar: '',
    image_path: '',
    image_paths: [],
    main_media_type: '',
  });
  const [updateMainImage, setUpdateMainImage] = useState(null);
  const [updateAdditionalImages, setUpdateAdditionalImages] = useState([]);
  const [updateMediaOrder, setUpdateMediaOrder] = useState([]);
  const [updateError, setUpdateError] = useState('');
  const [updateSuccessMessage, setUpdateSuccessMessage] = useState('');
  const { moveItem, orderMessage, savingOrder } = useItemOrdering(images, setImages, `${API_BASE_URL}/gallery/order`);

  const normalizePath = (value) => String(value || '').trim();

  const getAdditionalPaths = (galleryItem = {}) => {
    if (Array.isArray(galleryItem.media_items) && galleryItem.media_items.length > 0) {
      return galleryItem.media_items
        .filter((media) => !media?.is_main)
        .map((media) => normalizePath(media?.path || media?.image_path))
        .filter(Boolean);
    }

    const mainPath = normalizePath(galleryItem.main_image || galleryItem.image_path);
    return (Array.isArray(galleryItem.image_paths) ? galleryItem.image_paths : [])
      .map((mediaPath) => normalizePath(mediaPath))
      .filter((mediaPath) => Boolean(mediaPath) && mediaPath !== mainPath);
  };

  const isVideoPath = (path) => /\.(mp4|mov|avi|mkv|webm)$/i.test(path || '');

  const renderMediaThumb = (media, key, style) => {
    const path = typeof media === 'string' ? media : (media?.path || media?.image_path || '');
    const isVideo = (typeof media === 'object' && media?.media_type === 'video') || isVideoPath(path);
    const src = path ? `${API_BASE_URL}/${path}` : '';
    if (!src) return null;
    return isVideo ? (
      <video key={key} src={src} controls style={style} />
    ) : (
      <img key={key} src={src} alt={path} style={style} />
    );
  };

  
  const handleImageChange = (e) => {
    const image = e.target.files[0];
    setNewMainImage(image);
  };

  const handleAdditionalImagesChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setAdditionalImages(Array.from(e.target.files));
    } else {
      setAdditionalImages([]);
    }
  };

  const moveAdditionalImage = (index, direction) => {
    setAdditionalImages((items) => moveArrayItem(items, index, direction));
  };

  const moveUpdateMedia = (index, direction) => {
    setUpdateMediaOrder((items) => moveArrayItem(items, index, direction));
  };

  const handleUpdate = (image) => {
    const currentAdditionalPaths = getAdditionalPaths(image);
    setUpdateGallery({
      id: image.id,
      title: image.title || '',
      title_ar: image.title_ar || '',
      description: image.description || '',
      description_ar: image.description_ar || '',
      image_path: image.main_image || image.image_path || '',
      image_paths: currentAdditionalPaths,
      main_media_type: image.main_media_type || '',
    });
    setUpdateMainImage(null);
    setUpdateAdditionalImages([]);
    setUpdateMediaOrder(
      currentAdditionalPaths.map((mediaPath, index) => ({
        key: `existing-${index}-${mediaPath}`,
        type: 'existing',
        value: mediaPath,
      }))
    );
    setUpdateError('');
    setUpdateSuccessMessage('');
    setShowUpdateForm(true);
  };

  const handleUpdateChange = (e) => {
    const { name, value } = e.target;
    setUpdateGallery((prev) => ({ ...prev, [name]: value }));
  };

  const handleUpdateMainImageChange = (e) => {
    const file = e.target.files && e.target.files[0];
    setUpdateMainImage(file || null);
  };

  const handleUpdateAdditionalImagesChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const files = Array.from(e.target.files);
      setUpdateAdditionalImages(files);
      setUpdateMediaOrder((items) => [
        ...items.filter((item) => item.type === 'existing'),
        ...files.map((file, index) => ({
          key: `new-${index}-${file.name}-${file.lastModified}`,
          type: 'new',
          file,
          fileIndex: index,
        })),
      ]);
    } else {
      setUpdateAdditionalImages([]);
      setUpdateMediaOrder((items) => items.filter((item) => item.type === 'existing'));
    }
  };

  const handleRemoveCurrentAdditionalImage = (targetPath) => {
    const normalizedTarget = normalizePath(targetPath);
    if (!normalizedTarget) return;

    setUpdateGallery((prev) => ({
      ...prev,
      image_paths: (prev.image_paths || []).filter(
        (existingPath) => normalizePath(existingPath) !== normalizedTarget
      ),
    }));
    setUpdateMediaOrder((items) => items.filter(
      (item) => !(item.type === 'existing' && normalizePath(item.value) === normalizedTarget)
    ));
  };

  const handleRemoveOrderedUpdateMedia = (item) => {
    if (item.type === 'existing') {
      handleRemoveCurrentAdditionalImage(item.value);
      return;
    }

    setUpdateAdditionalImages((files) => files.filter((_, index) => index !== item.fileIndex));
    setUpdateMediaOrder((items) => items
      .filter((current) => current.key !== item.key)
      .map((current) => current.type === 'new' && current.fileIndex > item.fileIndex
        ? { ...current, fileIndex: current.fileIndex - 1 }
        : current));
  };

  const handleGalleryUpdate = async (e) => {
    e.preventDefault();
    if (!updateGallery.id) return;
    try {
      const formData = new FormData();
      formData.append('title', updateGallery.title);
      formData.append('title_ar', updateGallery.title_ar);
      formData.append('description', updateGallery.description);
      formData.append('description_ar', updateGallery.description_ar);

      if (updateMainImage) {
        formData.append('main_image', updateMainImage);
      }

      formData.append(
        'retained_additional_images_json',
        JSON.stringify(updateGallery.image_paths || [])
      );

      (updateGallery.image_paths || []).forEach((existingPath) => {
        formData.append('retained_additional_images', existingPath);
      });

      updateAdditionalImages.forEach((file) => {
        formData.append('additional_images', file);
      });
      formData.append(
        'additional_media_order_json',
        JSON.stringify(updateMediaOrder.map((item) =>
          item.type === 'existing' ? `existing:${item.value}` : `new:${item.fileIndex}`
        ))
      );

      await axios.put(`${API_BASE_URL}/gallery/update/${updateGallery.id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setUpdateSuccessMessage('Gallery updated successfully');
      setTimeout(() => setUpdateSuccessMessage(''), 4000);
      setShowUpdateForm(false);
      fetchImages();
    } catch (err) {
      console.error('Error updating gallery:', err);
      setUpdateError('Error updating gallery');
      setTimeout(() => setUpdateError(''), 4000);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const formData = new FormData();
      formData.append('main_image', newMainImage);
      additionalImages.forEach((file) => {
        formData.append('additional_images', file);
      });
      formData.append('title', newTitle);
      formData.append('title_ar', newTitleAr);
      formData.append('description', newDescription);
      formData.append('description_ar', newDescriptionAr);

      await axios.post(`${API_BASE_URL}/gallery/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Refresh the list of images after successful upload
      fetchImages();
      setNewMainImage(null);
      setAdditionalImages([]);
      setNewTitle('');
      setNewTitleAr('');
      setNewDescription('');
      setNewDescriptionAr('');
    } catch (error) {
      console.error('Error uploading image:', error);
    }
  };

  const fetchImages = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/gallery/images`);
      setImages(response.data.data);
      setgalleryCount(response.data.data.length); 
      setLoading(false);
      setError(null);
    } catch (error) {
      console.error(`Error fetching images: ${error}`);
      setLoading(false);
      setError('Error fetching images. Please try again later.');
    }
  };

 

  const handleDelete = async (imageId) => {
    try {
      await axios.delete(`${API_BASE_URL}/gallery/delete/${imageId}`);
      // Remove the deleted image from the state
      setImages((prevImages) => 
      prevImages.filter((image) => image.id !== imageId));
    } catch (error) {
      console.error('Error deleting image:', error);
    }
  };
  useEffect(() => {
    fetchImages();
  }, []);

  console.log("Images:", images);


  return (
    <>
      <div className="content">
        <Form onSubmit={handleSubmit}>
          <Row>
            <Col md="6">
              <FormGroup>
                <label className="mt-2">Title (English):</label>
                <Input type="text" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} />
                <label className="mt-2">Title (Arabic):</label>
                <Input type="text" value={newTitleAr} onChange={(e) => setNewTitleAr(e.target.value)} />
                <label className="mt-2">Description (English):</label>
                <Input type="textarea" value={newDescription} onChange={(e) => setNewDescription(e.target.value)} />
                <label className="mt-2">Description (Arabic):</label>
                <Input type="textarea" value={newDescriptionAr} onChange={(e) => setNewDescriptionAr(e.target.value)} />
                 <label>Main Image:</label>
                <Input type="file" name="main_image" onChange={handleImageChange} />
                <label className="mt-2">Additional Images (optional):</label>
                <Input type="file" name="additional_images" multiple onChange={handleAdditionalImagesChange} />

                <GalleryMediaOrderEditor
                  title="Additional Images Order (main image always appears first)"
                  items={additionalImages}
                  onMove={moveAdditionalImage}
                  renderPreview={renderMediaThumb}
                />

                <Button className="btn-round" color="primary" type="submit">
                  Add Item
                </Button>
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md="12">
              <div className="update ml-auto mr-auto">
                
              </div>
            </Col>
          </Row>
        </Form>
        <CardFooter>
          <div>Total Exhibition: {galleryCount}</div>
        </CardFooter>
   
        <Row>
          <Col md="12">
            <Card>
              <CardHeader>
                <CardTitle tag="h4">Image Exhibition</CardTitle>
              </CardHeader>
              <CardBody>
                <ItemOrderMessage message={orderMessage} />
                <Table responsive>
                <thead className="text-primary">
                    <tr>
                      {/* <th>ID</th> */}
                      <th>Image</th>
                      <th>Title (EN)</th>
                      <th>Title (AR)</th>
                      <th>Description (EN)</th>
                      <th>Description (AR)</th>
                      <th>Additional Images</th>
                      <th>Order</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                <tbody>
                {images.map((image, index) => (
  <tr key={image.id}>
    <td>
      {(() => {
        const mainPath = image.main_image || image.image_path;
        const isVideo = image.main_media_type === 'video' || isVideoPath(mainPath);
        const src = mainPath ? `${API_BASE_URL}/${mainPath}` : '';
        if (!src) return null;
        return isVideo ? (
          <video src={src} controls style={{ width: '200px', height: '200px', objectFit: 'cover' }} />
        ) : (
          <img src={src} alt={mainPath} style={{ width: '200px', height: '200px', objectFit: 'cover' }} />
        );
      })()}
    </td>
    <td>{image.title || ''}</td>
    <td>{image.title_ar || ''}</td>
    <td>{image.description || ''}</td>
    <td>{image.description_ar || ''}</td>
    <td>
      {(image.media_items && image.media_items.length > 0) || (image.image_paths && image.image_paths.length > 0) ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 80px)', gap: '8px' }}>
          {(image.media_items && image.media_items.length > 0
            ? image.media_items.filter((m) => !m.is_main)
            : image.image_paths
          ).map((media, idx) =>
            renderMediaThumb(
              media,
              `${image.id}-add-${idx}`,
              { width: '80px', height: '80px', objectFit: 'cover' }
            )
          )}
        </div>
      ) : (
        <span>-</span>
      )}
    </td>
    <td><ItemOrderControls index={index} count={images.length} onMove={moveItem} disabled={savingOrder} /></td>
    <td>
      <Button color="success" onClick={() => handleUpdate(image)} style={{ marginRight: '8px' }}>Update</Button>
      <Button color="danger" onClick={() => handleDelete(image.id)}>Delete</Button>
    </td>
  </tr>
))}

</tbody>
                </Table>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
      {showUpdateForm && (
        <div className="content">
          <Form onSubmit={handleGalleryUpdate}>
            <Row>
              <Col md="7">
                <FormGroup>
                  <label>Title (English):</label>
                  <Input
                    type="text"
                    name="title"
                    value={updateGallery.title}
                    onChange={handleUpdateChange}
                  />
                </FormGroup>
              </Col>
              <Col md="7">
                <FormGroup>
                  <label>Title (Arabic):</label>
                  <Input
                    type="text"
                    name="title_ar"
                    value={updateGallery.title_ar}
                    onChange={handleUpdateChange}
                  />
                </FormGroup>
              </Col>
            </Row>
            <Col md="7">
              <FormGroup>
                <label>Description (English):</label>
                <Input
                  type="textarea"
                  name="description"
                  value={updateGallery.description}
                  onChange={handleUpdateChange}
                />
              </FormGroup>
            </Col>
            <Col md="7">
              <FormGroup>
                <label>Description (Arabic):</label>
                <Input
                  type="textarea"
                  name="description_ar"
                  value={updateGallery.description_ar}
                  onChange={handleUpdateChange}
                />
              </FormGroup>
            </Col>
            <Row>
              <Col md="7">
                <FormGroup>
                  <label>Current Main Image:</label>
                  {updateGallery.image_path && (
                    <div>
                      {renderMediaThumb(
                        { path: updateGallery.image_path, media_type: updateGallery.main_media_type },
                        `${updateGallery.id}-main`,
                        { maxWidth: '200px', marginBottom: '10px' }
                      )}
                    </div>
                  )}
                  <label>Change Main Image:</label>
                  <Input type="file" name="main_image" onChange={handleUpdateMainImageChange} />
                </FormGroup>
              </Col>
              <Col md="7">
                <FormGroup>
                  <label>Current Additional Images:</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                    {(updateGallery.image_paths || []).map((path, index) => (
                      <div
                        key={`${updateGallery.id}-cur-${index}`}
                        style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
                      >
                        {renderMediaThumb(
                          path,
                          `${updateGallery.id}-cur-thumb-${index}`,
                          { width: '100px', height: '100px', objectFit: 'cover' }
                        )}
                        <Button
                          color="danger"
                          size="sm"
                          style={{ marginTop: '6px' }}
                          onClick={() => handleRemoveCurrentAdditionalImage(path)}
                          type="button"
                        >
                          Remove
                        </Button>
                      </div>
                    ))}
                    {(updateGallery.image_paths || []).length === 0 && (
                      <span style={{ color: '#666' }}>No additional images</span>
                    )}
                  </div>
                  <label>New Additional Images (optional):</label>
                  <Input type="file" name="additional_images" onChange={handleUpdateAdditionalImagesChange} multiple />

                  <GalleryMediaOrderEditor
                    title="Additional Images Order (current and new)"
                    items={updateMediaOrder}
                    onMove={moveUpdateMedia}
                    onRemove={handleRemoveOrderedUpdateMedia}
                    renderPreview={renderMediaThumb}
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md="12">
                <div className="update ml-auto mr-auto">
                  <Button className="btn-round" color="primary" type="submit">
                    Update Item
                  </Button>{' '}
                  <Button
                    className="btn-round"
                    color="secondary"
                    onClick={() => {
                      setShowUpdateForm(false);
                      setUpdateMainImage(null);
                      setUpdateAdditionalImages([]);
                      setUpdateMediaOrder([]);
                      setUpdateGallery({
                        id: null,
                        title: '',
                        title_ar: '',
                        description: '',
                        description_ar: '',
                        image_path: '',
                        image_paths: [],
                        main_media_type: '',
                      });
                    }}
                  >
                    Cancel
                  </Button>
                  {updateError && (
                    <div style={{ color: 'red', marginTop: '10px' }}>
                      {updateError}
                    </div>
                  )}
                  {updateSuccessMessage && (
                    <div className="alert alert-success" role="alert" style={{ width: '50%', margin: '10px auto', textAlign: 'center' }}>
                      {updateSuccessMessage}
                    </div>
                  )}
                </div>
              </Col>
            </Row>
          </Form>
        </div>
      )}
    </>
  );
}

export default Gallery;
